import React, { useState } from 'react';
import { Lock, Trophy } from 'lucide-react';
import { Tournament, Player, GolfCourse } from '../types';
import { MatchPlay } from '../types/matchplay';
import { courses as defaultCourses } from '../data/courses';
import { useRegistration } from '../contexts/RegistrationContext';
import { TournamentSelector } from './TournamentSelector';
import { ImportData } from './ImportData';
import { GroupScoring } from './GroupScoring';
import { Leaderboard } from './Leaderboard';
import { MatchPlaySetup } from './matchplay/MatchPlaySetup';
import { MatchPlayLeaderboard } from './matchplay/MatchPlayLeaderboard';
import { MatchPlayScoring } from './matchplay/MatchPlayScoring';
import { MatchplayDashboard } from './matchplay-v2/MatchplayDashboard';
import { HCCupStandings } from './HCCupStandings';
import { supabase } from '../lib/supabase';
import { TXCupStandings } from './TXCupStandings';
import { MembersProfile } from './members/MembersProfile';
import { SetupErrorBoundary } from './ErrorBoundary';
import { RegistrationSection } from './registration/RegistrationSection';
import { generateUUID } from '../lib/utils/uuid';

interface MainContentProps {
  activeTab: 'tournaments' | 'scoring' | 'leaderboard' | 'import' | 'matchplay' | 'matchplay-v2' | 'members' | 'tx-cup' | 'registration';
  isAdmin: boolean;
  tournaments: Tournament[];
  players: Player[];
  selectedTournament: Tournament | null;
  onTournamentSelect: (tournament: Tournament) => void;
  onCreateTournament: () => void;
  onDeleteTournament: (tournament: Tournament) => void;
  onTournamentUpdate: (tournament: Tournament) => void;
  onPlayersImported: (players: Player[]) => void;
  currentCourse: GolfCourse | null;
  selectedPlayers: Player[];
  setSelectedPlayers: (players: Player[]) => void;
  scores: Record<string, Record<number, number>>;
  setScores: (scores: Record<string, Record<number, number>>) => void;
  isSelectingPlayers: boolean;
  setIsSelectingPlayers: (isSelecting: boolean) => void;
  currentHoleIndex: number;
  setCurrentHoleIndex: (index: number) => void;
  onScoreSubmit: (scores: Record<string, any>) => void;
  onResetScoring: () => void;
  completedHoles: number[];
  setCompletedHoles: (holes: number[]) => void;
  matches: MatchPlay[];
  onMatchesUpdate: (matches: MatchPlay[]) => void;
  activeMatch?: string;
  year?: string;
  setActiveMatch: (matchId?: string) => void;
  membersData: any[];
  setMembersData: (data: any[]) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  startRegistration: () => void;
}

export function MainContent({
  activeTab,
  isAdmin,
  tournaments,
  players,
  selectedTournament,
  onTournamentSelect,
  onCreateTournament,
  onDeleteTournament,
  onTournamentUpdate,
  onPlayersImported,
  currentCourse,
  selectedPlayers,
  setSelectedPlayers,
  scores,
  setScores,
  isSelectingPlayers,
  setIsSelectingPlayers,
  currentHoleIndex,
  setCurrentHoleIndex,
  onScoreSubmit,
  onResetScoring,
  completedHoles,
  setCompletedHoles,
  matches,
  onMatchesUpdate,
  activeMatch,
  year = '2025',
  setActiveMatch,
  membersData,
  setMembersData,
  searchTerm,
  setSearchTerm,
}: MainContentProps) {
  const [selectedYear, setSelectedYear] = useState('2025');
  const [isOfficialTournament, setIsOfficialTournament] = useState(true);
  const { startRegistration } = useRegistration();

  return (
    <>
      {activeTab === 'tournaments' && (
        <TournamentSelector
          tournaments={tournaments}
          players={players.filter(p => p.tournamentId === selectedTournament?.id)}
          selectedTournament={selectedTournament}
          onTournamentSelect={onTournamentSelect}
          onCreateTournament={onCreateTournament}
          onDeleteTournament={onDeleteTournament}
          isAdmin={isAdmin}
        />
      )}

      {activeTab === 'import' && (
        <SetupErrorBoundary>
          <ImportData
            selectedTournament={selectedTournament}
            onTournamentUpdate={onTournamentUpdate}
            onPlayersImported={onPlayersImported}
            onCoursesImported={() => {}}
            initialPlayers={players}
            year={selectedYear}
            isOfficialTournament={isOfficialTournament}
            onYearChange={setSelectedYear}
            onOfficialTournamentChange={setIsOfficialTournament}
            onCourseSelect={(courseId) => {
              const course = defaultCourses.find(c => c.id === courseId);
              if (course && selectedTournament) {
                onTournamentUpdate({
                  ...selectedTournament,
                  courseId,
                  selectedTee: course.tees[0].name
                });
              }
            }}
            onTeeSelect={(teeName) => {
              if (selectedTournament) {
                onTournamentUpdate({
                  ...selectedTournament,
                  selectedTee: teeName
                });
              }
            }}
            selectedCourseId={selectedTournament?.courseId || ''}
            selectedTee={selectedTournament?.selectedTee || ''}
            onFormatSelect={(format) => {
              if (selectedTournament) {
                onTournamentUpdate({
                  ...selectedTournament,
                  format
                });
              }
            }}
            selectedFormat={selectedTournament?.format || 'stroke'}
            onReset={() => {
              if (!isAdmin) return;
              window.location.reload();
            }}
            isAdmin={isAdmin}
            supabase={supabase}
          />
        </SetupErrorBoundary>
      )}

      {/* Registration Section */}
      {activeTab === 'registration' && selectedTournament && selectedTournament.status === 'registration' && (
        <RegistrationSection
          tournament={selectedTournament}
          isAdmin={isAdmin}
          onTournamentUpdate={onTournamentUpdate}
          onStartRegistration={(tournamentId) => startRegistration(tournamentId)}
        />
      )}

      {activeTab === 'scoring' && currentCourse && selectedTournament?.format === 'stroke' && (
        <>
          {selectedTournament.status === 'completed' ? (
            // If tournament is completed, redirect to leaderboard
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Tournament Completed
                  </h3>
                  <p className="text-gray-600 max-w-sm mx-auto">
                    This tournament has been completed. No further scoring is allowed.
                  </p>
                  <button
                    onClick={() => {
                      const redirectEvent = new CustomEvent('redirectToLeaderboard', {
                        detail: { tournamentId: selectedTournament.id }
                      });
                      window.dispatchEvent(redirectEvent);
                    }}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    View Leaderboard
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <GroupScoring
              players={players}
              course={currentCourse}
              isAdmin={isAdmin}
              selectedPlayers={selectedPlayers}
              setSelectedPlayers={setSelectedPlayers}
              scores={scores}
              setScores={setScores}
              isSelectingPlayers={isSelectingPlayers}
              setIsSelectingPlayers={setIsSelectingPlayers}
              currentHoleIndex={currentHoleIndex}
              setCurrentHoleIndex={setCurrentHoleIndex}
              onScoreSubmit={onScoreSubmit}
              onResetScoring={onResetScoring}
              completedHoles={completedHoles}
              setCompletedHoles={setCompletedHoles}
              tournament={selectedTournament!}
              selectedTee={currentCourse.tees.find(t => t.name === selectedTournament.selectedTee)}
              onTournamentUpdate={onTournamentUpdate}
              startRegistration={startRegistration}
            />
          )}
        </>
      )}

      {activeTab === 'leaderboard' && currentCourse && (
        <Leaderboard
          players={players}
          tournament={selectedTournament!}
          courses={defaultCourses}
          isAdmin={isAdmin}
          matches={matches}
          onTournamentUpdate={onTournamentUpdate}
          activeTab={activeTab}
        />
      )}

      {activeTab === 'matchplay' && currentCourse && selectedTournament?.format === 'match' && (
        <div className="space-y-6">
          {!activeMatch ? (
            <>
              <MatchPlaySetup
                players={players}
                onMatchCreate={(match) => {
                  const newMatch: MatchPlay = {
                    ...match,
                    id: generateUUID(),
                    holes: [],
                    status: 'active'
                  };
                  onMatchesUpdate([...matches, newMatch]);
                }}
                selectedTournament={selectedTournament}
              />
              <MatchPlayLeaderboard
                matches={matches}
                onMatchSelect={setActiveMatch}
                selectedTournament={selectedTournament}
              />
            </>
          ) : (
            <MatchPlayScoring
              match={matches.find(m => m.id === activeMatch)!}
              course={currentCourse}
              onScoreSubmit={(hole, result) => {
                const updatedMatches = matches.map(m => {
                  if (m.id === activeMatch) {
                    const existingHoleIndex = m.holes.findIndex(h => h.number === hole);
                    const updatedHoles = [...m.holes];
                    if (existingHoleIndex >= 0) {
                      updatedHoles[existingHoleIndex] = result;
                    } else {
                      updatedHoles.push(result);
                    }
                    return {
                      ...m,
                      holes: updatedHoles
                    };
                  }
                  return m;
                });
                onMatchesUpdate(updatedMatches);
              }}
              onMatchComplete={(match) => {
                const updatedMatches = matches.map(m => 
                  m.id === match.id ? match : m
                );
                onMatchesUpdate(updatedMatches);
                setActiveMatch(undefined);
              }}
            />
          )}
        </div>
      )}

      {activeTab === 'matchplay-v2' && (
        <MatchplayDashboard />
      )}

      {activeTab === 'hc-standing' && (
        <HCCupStandings
          members={membersData}
          year={year}
          searchTerm={searchTerm}
          selectedTournament={tournaments.find(t => t.name === 'Jan \'25 Windrose')}
        />
      )}
      
      {activeTab === 'tx-cup' && (
        <TXCupStandings searchTerm={searchTerm} />
      )}

      {activeTab === 'members' && (
        <MembersProfile
          members={membersData}
          isAdmin={isAdmin}
          setMembers={setMembersData}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      )}
    </>
  );
}